'use client'

import { noOp } from '@/utils/functions/no-op'
import { createContext, useCallback, useContext, useRef } from 'react'

interface IPlayContext {
    register: (id: string, pauseFn: () => void) => void
    onPlay: (id: string) => void
    pauseAll: () => void
}

const PlayContext = createContext<IPlayContext>({
    register: noOp,
    onPlay: noOp,
    pauseAll: noOp,
})

interface IPlayContextProviderProps {
    children: React.ReactNode
}

export const PlayContextProvider = ({ children }: IPlayContextProviderProps) => {
    const idToPauseMapRef = useRef<Record<string, { pauseFn: () => void; mountedMs: number }>>({})

    const pauseAll = useCallback(() => {
        Object.values(idToPauseMapRef.current).forEach(({ pauseFn }) => {
            pauseFn()
        })
    }, [])

    const onPlay = useCallback((id: string) => {
        const otherIds = Object.keys(idToPauseMapRef.current).filter((key) => key !== id)

        otherIds.forEach((otherId) => {
            const pauseFn = idToPauseMapRef.current[otherId]?.pauseFn

            pauseFn?.()
        })
    }, [])

    const register = useCallback((id: string, pauseFn: () => void) => {
        idToPauseMapRef.current[id] = { pauseFn, mountedMs: Date.now() }

        return () => {
            const mountedMs = idToPauseMapRef.current[id]?.mountedMs

            // The function is automatically called in the return of the implementing useEffects,
            // which then leads to unwanted initial calls (useEffect cleanup is called initially)
            // In order to sort them out, we check if the component has been mounted for at least 200ms.
            // The useEffect cleanup is called after just a couple ms, so we'll be safe.
            // Sorry for this hack, but it's not worth investing that much more time into this.
            if (mountedMs && Date.now() - mountedMs > 200) {
                idToPauseMapRef.current[id]?.pauseFn()
            }

            delete idToPauseMapRef.current[id]
        }
    }, [])

    return <PlayContext.Provider value={{ register, onPlay, pauseAll }}>{children}</PlayContext.Provider>
}

export const usePlayContext = () => useContext(PlayContext)
